import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {DashboardButton, DashboardIconButton} from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import {cn} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";
import type {DashboardPopoverProps, MenuPos} from "@/ReactInit/Scripts/DashboardPage/types";

const GAP_PX = 6;
const VIEWPORT_PADDING = 8;
const MAX_MENU_HEIGHT = 280;

const DashboardPopover: React.FC<DashboardPopoverProps> = (
  {
    items,
    onSelect,
    label,
    disabled,
    variant = "default",
    className,
    icon = "icon-ellipsis",
    labelClass,
    loading,
    loadingLabel,
  }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPos | null>(null);

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);

  const portalRoot = useMemo(() => {
    if (typeof document === "undefined") return null;
    return document.body;
  }, []);

  const computePosition = () => {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();

    const desiredTop = rect.bottom + GAP_PX;
    const desiredLeft = rect.right;
    const minWidth = Math.max(150, Math.round(rect.width));
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const initialLeft = Math.min(desiredLeft, viewportW - VIEWPORT_PADDING);
    const top = Math.min(desiredTop, viewportH - VIEWPORT_PADDING);

    const maxHeight = Math.min(
      MAX_MENU_HEIGHT,
      Math.max(120, viewportH - top - VIEWPORT_PADDING)
    );

    setPos({top, left: initialLeft, minWidth, maxHeight});
  };

  useLayoutEffect(() => {
    if (!open) return;
    computePosition();
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    if (!pos) return;
    const menu = menuRef.current;
    if (!menu) return;

    const menuRect = menu.getBoundingClientRect();
    const viewportW = window.innerWidth;

    const trigger = triggerRef.current;
    if (!trigger) return;
    const triggerRect = trigger.getBoundingClientRect();
    const desiredLeft = triggerRect.right - menuRect.width;

    const clampedLeft = Math.max(
      VIEWPORT_PADDING,
      Math.min(desiredLeft, viewportW - menuRect.width - VIEWPORT_PADDING)
    );

    if (Math.round(clampedLeft) !== Math.round(pos.left)) {
      setPos((p) => (p ? {...p, left: clampedLeft} : p));
    }
  }, [open, pos]);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    const onScroll = () => computePosition();
    const onResize = () => computePosition();

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const Trigger = (
    <>
      {variant === "default" ? (
        <DashboardIconButton
          label={label}
          onClick={() => {
            if (disabled) return;
            setOpen((o) => !o);
          }}
          icon={icon}
          disabled={disabled}
          variant="primary"
          rounded="small"
          className={className ?? ""}
        />
      ) : (
        <DashboardButton
          label={label}
          labelClass={labelClass ?? ""}
          onClick={() => {
            if (disabled) return;
            setOpen((o) => !o);
          }}
          icon={icon}
          disabled={disabled}
          variant="underline"
          className={className ?? ""}
          loading={loading}
          loadingText={loadingLabel}
          noPadding
        />
      )}
    </>
  );

  const Menu = open && pos && portalRoot
    ? createPortal(
      <ul
        ref={menuRef}
        role="menu"
        className={cn(
          "fixed z-[9999] bg-white border-2 shadow border-brown-secondary rounded-xl",
          "overflow-y-auto [&>li+li]:border-t [&>li+li]:border-t-disabled-bg"
        )}
        style={{
          top: pos.top,
          left: pos.left,
          minWidth: pos.minWidth,
          maxHeight: pos.maxHeight,
        }}
      >
        {items.map((it, index) => (
          <li
            key={it.value ?? it.label ?? index}
            role="menuitem"
            aria-disabled={it.disabled ? true : undefined}
            onClick={() => {
              if (it.disabled) return;
              onSelect(it.value ?? it.label);
              setOpen(false);
            }}
            className={cn(
              "px-5 py-3 cursor-pointer hover:bg-yellow-25 whitespace-nowrap",
              it.disabled && "opacity-50 cursor-not-allowed hover:bg-transparent"
            )}
          >
            {it.label}
          </li>
        ))}
      </ul>,
      portalRoot
    )
    : null;

  return (
    <div ref={triggerRef} className="inline-flex">
      {Trigger}
      {Menu}
    </div>
  );
};

export default DashboardPopover;
